import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SimpleCalendar } from '../ui/simple-calendar'; // Assuming this exists and is theme-aware
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import { useTheme } from '../ui/theme-provider';
import { Alert, AlertDescription } from "../ui/alert";
import { encryptData } from '../../encryptionUtil'; // Import encryptData

const RegistrationRenewalModal = ({ isOpen, onClose, registrationType, currentData, userId, onUpdateSuccess }) => {
  const [provider, setProvider] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const { toast } = useToast();
  const { theme } = useTheme();

  useEffect(() => {
    if (isOpen && currentData) {
      setProvider(currentData.provider || (registrationType === 'gdc' ? 'General Dental Council' : ''));
      // Ensure currentData.expiryDate is handled correctly whether it's a Timestamp or null
      const currentExpiry = currentData.expiryDate;
      setExpiryDate(currentExpiry?.toDate ? currentExpiry.toDate() : (currentExpiry ? new Date(currentExpiry) : null));
    } else if (isOpen && registrationType === 'gdc') {
      setProvider('General Dental Council');
      setExpiryDate(null); // Reset expiry date when opening for GDC without current data
    } else if (isOpen) {
      setProvider(''); // Reset provider for insurance if no current data
      setExpiryDate(null); // Reset expiry date
    }
    // Reset error when modal opens or type changes
    setError(null);
  }, [isOpen, currentData, registrationType]);

  const handleDateSelect = (date) => {
    if (date) {
      setExpiryDate(date);
      setIsDatePickerOpen(false); // Close picker after selection
    }
  };

  const handleSave = async () => {
    if (!userId) {
      setError("User not identified. Cannot save.");
      return;
    }
    if (!expiryDate) {
      setError("Please select an expiry date.");
      return;
    }
    if (registrationType === 'insurance' && !provider.trim()) {
      setError("Please enter the insurance provider name.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userDocRef = doc(db, 'users', userId);
      const updateData = {};

      if (registrationType === 'gdc') {
        updateData.gdcProvider = provider.trim() || 'General Dental Council'; // Ensure GDC provider is set
        updateData.gdcExpiryDate = Timestamp.fromDate(expiryDate);
        updateData.gdcLastUpdated = Timestamp.now();
      } else if (registrationType === 'insurance') {
        // Encrypt the insurance provider
        updateData.insuranceProvider = encryptData(provider.trim());
        updateData.insuranceExpiryDate = Timestamp.fromDate(expiryDate);
        updateData.insuranceLastUpdated = Timestamp.now();
      } else {
        throw new Error("Invalid registration type");
      }
      
      // Remove old professionalRegistrations structure if it exists, to be safe, though merge should handle it.
      // updateData['professionalRegistrations'] = FieldValue.delete(); // Might be too aggressive if other data is there.
      // Better to ensure ProfileSettings and other places no longer write the old structure.

      console.log("Attempting to update Firestore with (top-level):", updateData);

      await updateDoc(userDocRef, updateData);

      console.log("Firestore update successful."); // Log success

      toast({
        title: "Success",
        description: `${registrationType === 'gdc' ? 'GDC' : 'Insurance'} details updated.`,
        variant: "success",
      });

      if (onUpdateSuccess) {
        // Pass back the saved data structure - This triggers the flag clearing in DashboardPage
        onUpdateSuccess({
          provider: provider.trim(),
          expiryDate: Timestamp.fromDate(expiryDate),
          lastUpdated: Timestamp.now()
        });
      }
      // onClose(); // Let the success handler close the modal

    } catch (err) {
      console.error("Error updating registration details:", err); // Log the specific error
      setError(`Failed to save details: ${err.message}. Please try again.`); // Show more specific error
      toast({
        title: "Error",
        description: "Could not save registration details.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const title = registrationType === 'gdc' ? 'GDC Registration' : 'Indemnity Insurance';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E] border-purple-500/30'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(theme === 'light' ? 'text-gray-900' : 'text-white')}>{`Update ${title}`}</DialogTitle>
          <DialogDescription className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>
            Update your {title.toLowerCase()} expiry information.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className={cn(
             "mb-4",
             theme === 'light' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-red-900/20 border-red-500/50 text-red-300'
          )}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="provider" className={cn(theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Provider</Label>
            <Input
              id="provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              // Add direct inline styles with !important for light mode hover/focus
              style={theme === 'light' ? {
                "--tw-bg-opacity": "1 !important",
                backgroundColor: "white !important",
                color: "black !important", 
                borderColor: "rgb(209 213 219) !important" // Equivalent to border-gray-300
              } : {}}
              // Keep minimal classes
              className="mt-1"
              readOnly={registrationType === 'gdc'} // GDC provider is fixed
            />
          </div>
          <div>
            {/* Apply theme-aware styles */}
            <Label htmlFor="expiryDate" className={cn(theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Expiry Date</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal mt-1",
                    !expiryDate && (theme === 'light' ? "text-gray-500" : "text-muted-foreground"),
                    // Apply theme-aware styles
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black hover:bg-gray-50' 
                      : 'bg-[#2A2A3E] border-gray-700 text-white hover:bg-[#3a3a4e]'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expiryDate ? format(expiryDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              {/* Apply theme-aware styles */}
              <PopoverContent className={cn(
                "w-auto p-0",
                 theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
              )}>
                <SimpleCalendar
                  value={expiryDate}
                  onChange={handleDateSelect}
                  // Assume SimpleCalendar handles its own theme or inherits
                  className="border-none" 
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          {/* Cancel Button: Use default outline variant which should be themed globally */}
          <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancel</Button>
          {/* Save Button: Apply primary button styling for theme consistency */}
          <Button 
            onClick={handleSave} 
            disabled={isLoading}
            // Add primary button classes - bg-primary should be handled by globals.css
            className={cn(
              "bg-primary text-primary-foreground hover:bg-primary/90", // Base Shadcn primary styles
              // Add app-specific class if needed, but bg-primary should work via globals.css overrides
              "app-accent-purple-bg" // Ensure this class is applied for global overrides
            )}
          >
            {isLoading ? <LoadingSpinner size="small" /> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationRenewalModal;
